import type { LoaderFunctionArgs } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { rudderStack } from '~/utils/rudder-stack.server'
import { CallToAction } from '~/utils/a-b'
import {
  CallOutIllustration,
  CallOutList,
  CallOutText,
  Card,
  H4,
  Stack,
} from '~/components-v2'
import styles from './style.css?url'
import heroStyles from '~/components-v2/hero/hero.css?url'
import quoteStyles from '~/components/quote-section/quote-section.css?url'
import sheetPreviewStyles from '~/styles/components/sheet-preview.css?url'
import { CallOutItem } from '~/components-v2/callout-section/callout-section'
import { FooterDemoSection, G2Badges, fragmentLinks } from '~/fragments'

import { QuoteSection } from '~/components/quote-section'
import { QuoteTrint4 } from '~/config/quotes'

import { SheetGridPreview } from '~/components/illustration/business-intelligence'
import {
  CustomersMarquee,
  customersMarqueeLinks,
} from '~/components/shared/customers-marquee'

import { getBlogPostsByCategory } from '~/services/contentful'
import { useTypedLoaderData } from 'remix-typedjson'
import { CalcsTour } from '~/fragments/product-tour'
import {
  PostCollection,
  postCollectionLinks,
} from '~/components-v2/post-collection/post-collection'

const title = 'Analyze real-time data with Excel formulas'
const subTitle =
  'Get the familiarity of spreadsheet formulas with all the benefits of BI.'

export const loader = async ({ request }: LoaderFunctionArgs) => {
  rudderStack(request, title)

  const posts = await getBlogPostsByCategory({
    category: 'calculations',
    limit: 3,
  })
  return { title, subTitle, posts }
}
export { meta } from '~/utils'
export const links = () => [
  { rel: 'stylesheet', href: heroStyles },
  { rel: 'stylesheet', href: quoteStyles },
  { rel: 'stylesheet', href: sheetPreviewStyles },
  ...fragmentLinks.productTour(),
  ...fragmentLinks.footerDemoSection(),
  ...fragmentLinks.g2badges(),
  ...customersMarqueeLinks(),
  ...postCollectionLinks(),
  { rel: 'stylesheet', href: styles },
]

export default function () {
  const { title, subTitle, posts } = useTypedLoaderData<typeof loader>()

  return (
    <>
      <header className="hero calculations product-shot-inline">
        <Stack gap={4} as="hgroup">
          <h1>{title}</h1>
          <h2>{subTitle}</h2>
          <CallToAction />
        </Stack>
        <div className="product-imagery">
          <div className="gradients">
            <div className="vibes"></div>
            <div className="dawn"></div>
          </div>
          <SheetGridPreview />
        </div>
      </header>

      <CustomersMarquee />

      <CalcsTour title="See Calculations in action" />

      <section className="calculations-multiple-tools">
        <hgroup className="centered-section-title">
          <h3>Omni = SUM(Spreadsheets, BI, AI)</h3>
          <p>
            Forget exporting CSVs from your BI tool. Run all your calculations
            on a single platform that makes it easy to track changes, update
            analyses, and ensure accurate results.
          </p>
        </hgroup>

        <CallOutList>
          <CallOutItem>
            <CallOutText>
              <hgroup>
                <H4 fontSize="xl" fontWeight={600}>
                  Excel-lent parity
                </H4>
                <p>
                  Our calculations have the{' '}
                  <Link to="https://docs.omni.co/docs/querying-and-sql/table-calculations">
                    exact same syntax
                  </Link>{' '}
                  as Excel, so you can confidently explore data the first time
                  you use Omni.
                </p>
              </hgroup>
            </CallOutText>
            <CallOutIllustration>
              <Card raised padding={4}>
                <img
                  src="/images/pages/calculations/excel-parity.webp"
                  alt="A data table with a menu showing various excel functions"
                  loading="lazy"
                />
              </Card>
            </CallOutIllustration>
          </CallOutItem>

          <CallOutItem>
            <CallOutText>
              <hgroup>
                <H4 fontSize="xl" fontWeight={600}>
                  Reliable, useful AI
                </H4>
                <p>
                  Focus on the answer you want — not the formula you need.
                  Describe what you’re looking for and{' '}
                  <Link to="https://docs.omni.co/docs/querying-and-sql/table-calculations#calculate-with-ai">
                    Calculations AI
                  </Link>{' '}
                  will use a large language model to help you insert the
                  relevant Excel formulas directly into your Omni table.
                </p>
              </hgroup>
            </CallOutText>
            <CallOutIllustration>
              <Card raised padding={4}>
                <img
                  src="/images/pages/calculations/calcs-ai.webp"
                  alt="A data table with a menu popover showing an input that can be used to ask AI to write a formula or calculation"
                  loading="lazy"
                />
              </Card>
            </CallOutIllustration>
          </CallOutItem>
          <CallOutItem>
            <CallOutText>
              <hgroup>
                <H4 fontSize="xl" fontWeight={600}>
                  Iterate faster with reusable calculations
                </H4>
                <p>
                  Move faster with familiar formulas and promote reusable
                  calculations — like ARR or Profit — to your organization’s{' '}
                  <Link to="/data-modeling">data model</Link> so you and your
                  team can use them in future analyses. Create and update
                  metrics and definitions quickly without writing SQL or waiting
                  for the data team.
                </p>
              </hgroup>
            </CallOutText>
            <CallOutIllustration>
              <Card raised padding={4}>
                <img
                  src="/images/pages/calculations/column-menu-options.webp"
                  alt="A data table with a menu showing options for a column. The promote to workbook option is highlighted."
                  loading="lazy"
                />
              </Card>
            </CallOutIllustration>
          </CallOutItem>

          <CallOutItem>
            <CallOutText>
              <hgroup>
                <H4 fontSize="xl" fontWeight={600}>
                  Annotate data in-line to quickly add context
                </H4>
                <p>
                  Write plain text into any calculations column to enhance your
                  financial modeling, commission planning, invoices, and more.
                  Quickly make manual adjustments, provide context, and add
                  relevant details that might not be reflected in your data.
                </p>
              </hgroup>
            </CallOutText>
            <CallOutIllustration>
              <Card raised padding={4}>
                <img
                  src="/images/pages/calculations/annotate-inline.webp"
                  alt="A omni data table with hand written annotations on the data"
                  loading="lazy"
                />
              </Card>
            </CallOutIllustration>
          </CallOutItem>
        </CallOutList>
      </section>

      <QuoteSection quote={QuoteTrint4} />

      <FooterDemoSection />
      <G2Badges />

      <section className="wide">
        <h2 className="with-divider">Related resources</h2>
        <PostCollection posts={posts} />
      </section>
    </>
  )
}
